import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, FormGroup, FormLabel, Spinner } from 'react-bootstrap';
import {
  Archive,
  CameraVideo,
  FileEarmarkBarGraph,
  FiletypeCsv,
  FileText,
  Folder,
  LightningChargeFill,
  PersonCircle,
  PlusLg,
  Trash,
  Files
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { apartmentTypes, downloadFile, transformApartmentDataToCsv, updateItemsInArray } from '../../helpers/global';
import useLocalization from '../../hooks/useLocalization';
import AppModal from '../common/AppModal';
import GoogleDriveManager from '../common/google-drive-manager/GoogleDriveManager';
import AddEditApartmentForm from './AddEditApartmentForm';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import ViewCustomersMatchingApartmentModal from './ViewCustomersMatchingApartmentModal';
import CircularProgressBar from '../common/circular-progress';
import CustomMultiSelect from '../common/CustomMultiSelect';
import EditApartmentReportModal from './EditApartmentReportModal';
import { reportService } from '../../services/reportServices';
import HorizontalProgress from '../common/HorizontalProgress';
import AlertModal from '../common/AlertModal';
import { apartmentServices } from '../../services/appartmentServices';
import RecordZoomMeetingModal from './RecordZoomMeetingModal';
import { APARTMENT_STATUS_LABELS } from '../../helpers/constants';
import LogModal from '../log/LogModal';

const AddGoogleDrive = ({ onAddDriveFolder, creatingDriveFolder }) => {
  const { translate } = useLocalization();

  return (
    <div style={{ height: '100%' }} className="border rounded  d-flex align-items-center justify-content-center">
      <div className="w-75 gap-2 d-flex flex-column justify-content-center align-items-center">
        <h6 className="text-center">This apartment does not have any associated google drive folder.</h6>
        <div className="d-flex justify-content-center w-100 gap-2">
          <Button
            variant="success"
            disabled={creatingDriveFolder}
            className="text-white d-flex align-items-center gap-1"
            size="sm"
            onClick={onAddDriveFolder}
          >
            {!creatingDriveFolder && <PlusLg />}{' '}
            <h6 className="mb-0 smallFont">
              {translate(creatingDriveFolder ? 'creating_drive_folder' : 'click_to_create_folder')}
            </h6>
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddEditApartmentModal = ({
  show,
  onHide,
  adminCompTable,
  editMode,
  initialData,
  onSubmit,
  showProgress,
  isSavingOnly,
  onDeleteApartmentClick,
  createDriveFolderInApartment,
  creatingDriveFolder,
  assigneUsers,
  creatingNewFolder,
  handleApartmentAfterReportCreateOrUpdate,
  onArchiveApartmentStatusChangeSuccess,
  handleApartmentAfterNoteUpdate,
  onDuplicateApartmentClick
}) => {
  let { translate } = useLocalization();
  const abortControllerRef = useRef(null);
  const [apartmentExtraInfo, setApartmentExtraInfo] = useState(() => {
    if (editMode) {
      return initialData;
    }
    // In adding a new apartment, exclude the realEstateAnalyst from the initialData
    const { realEstateAnalyst, ...appartmentExtraInfo } = initialData;
    return {
      ...appartmentExtraInfo
    };
  });
  const [apartment, setApartment] = useState({});
  const [customerPickerModalMeta, setCustomerPickerModalMeta] = useState(null);
  const [openDriveManager, setOpenDriveManager] = useState(false);

  const [customersMatchingApartment, setCustomersMatchingApartments] = useState([]);
  const [fetchingCustomersMatchingApartments, setFetchingCustomersMatchingApartments] = useState(false);
  const [viewCustomersMatchingApartment, setViewCustomersMatchingApartments] = useState(false);
  const [selectReportTemplateModalMeta, setSelectReportTemplateModalMeta] = useState(null);
  const [recordZoomMeetingModalMeta, setRecordZoomMeetingModalMeta] = useState(null);
  const [updatedApartmentValues, setUpdatedApartmentValues] = useState(null);
  const [csvDataFields, setCsvDataFields] = useState(null);
  const [logModalMeta, setLogModalMeta] = useState(null);
  const [archiveAlertMeta, setArchiveAlertMeta] = useState({ show: false, loading: false });

  const fetchReport = async () => {
    if (!initialData?._id) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    const requestBody = {
      filter: [{ key: 'apartment', value: initialData?._id }]
    };
    const { response, error } = await reportService.getReport(requestBody, signal);
    if (error) {
      return toast.error(error.message);
    }
    setApartmentExtraInfo({
      _id: initialData?._id,
      type: initialData?.type,
      status: initialData?.status || '',
      gushTatHelka: initialData?.gushTatHelka || '',
      driveFolder: initialData?.driveFolder || null,
      zoomMeetId: initialData?.zoomMeetId || null,
      zoomUserId: initialData?.zoomUserId || null,
      zoomMeetLink: initialData?.zoomMeetLink || null,
      zoomMeetingsMigrated: initialData?.zoomMeetingsMigrated || false,
      report: response?.results[0],
      notes: initialData?.notes || [],
      profileAssignments: initialData?.profileAssignments || [],
      isApartmentArchived: initialData?.isApartmentArchived || false
    });
  };

  useEffect(() => {
    if (!apartmentExtraInfo?.report) {
      fetchReport();
    }
  }, [initialData, apartmentExtraInfo?.report]);

  let apartmentType = useMemo(() => apartmentExtraInfo?.type, [apartmentExtraInfo]);

  const apartmentValues = useMemo(() => {
    if (!show || !apartmentType) return null;
    if (apartmentType === apartmentTypes[0].value)
      return {
        irrAppartments: editMode ? [initialData] : [{ realEstateAnalyst: initialData.realEstateAnalyst }]
      };
    if (apartmentType === apartmentTypes[1].value)
      return {
        pinuiBinuiIrrAppartments: editMode ? [initialData] : [{ realEstateAnalyst: initialData.realEstateAnalyst }]
      };
    if (apartmentType === apartmentTypes[2].value)
      return {
        paperApartmentIrrApartments: editMode ? [initialData] : [{ realEstateAnalyst: initialData.realEstateAnalyst }]
      };
  }, [apartmentType, show, initialData]);

  const saveApartment = useCallback(
    (saveAndClose = false, createApartmentInfo = {}) => {
      let apartmentInfo = { ...apartment, ...apartmentExtraInfo, ...createApartmentInfo };
      try {
        onSubmit(apartmentInfo, false, saveAndClose);
        setUpdatedApartmentValues(apartmentInfo);
      } catch (error) {
        setUpdatedApartmentValues(null);
      }
    },
    [apartment, onSubmit, apartmentExtraInfo]
  );

  const createNewFolder = useCallback(() => {
    let apartmentInfo = { ...apartment, ...apartmentExtraInfo };

    onSubmit(apartmentInfo, true);
  }, [apartment, onSubmit, apartmentExtraInfo]);

  const selectCustomers = () => {
    let selectedCustomer = customerPickerModalMeta?.selectedCustomer;
    setApartmentExtraInfo({
      ...apartmentExtraInfo,
      profileAssignments: selectedCustomer
    });
    setCustomerPickerModalMeta(null);
  };

  const onDriveFolderClick = () => {
    setOpenDriveManager(!openDriveManager);
  };

  const onCreateDriveFolderClick = () => {
    if (editMode) {
      createDriveFolderInApartment(apartmentExtraInfo?._id);
    }
  };

  const fetchCustomersMatchingApartments = async () => {
    try {
      setFetchingCustomersMatchingApartments(true);
      let apartmentId = apartmentExtraInfo?._id;
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.USERS_LIST,
        requestBody: {
          filter: {
            role: 'Customer'
          },
          apartmentId,
          attachApartments: true
        }
      });
      if (error) {
        return toast.error(error);
      }
      setCustomersMatchingApartments(response);
    } catch (error) {
      console.log(error);
    } finally {
      setFetchingCustomersMatchingApartments(false);
    }
  };

  const onUpdateCustomerProfile = customer => {
    setApartmentExtraInfo(prev => ({
      ...prev,
      profileAssignments: prev?.profileAssignments?.map(p =>
        p?.customer?._id === customer?._id ? { ...p, customer } : p
      )
    }));
    setCustomersMatchingApartments(prev =>
      prev.map(c => (c._id === customer?._id ? { ...c, ...customer } : c))
    );
  };

  const handleViewOrCreateReportModal = () => {
    if (apartmentExtraInfo?.report) {
      setApartmentExtraInfo({ ...apartmentExtraInfo, showEditReport: true });
    } else {
      setSelectReportTemplateModalMeta({
        show: true,
        apartmentExtraInfo
      });
    }
  };

  const handleRecordZoomMeeting = async () => {
    setRecordZoomMeetingModalMeta({ ...recordZoomMeetingModalMeta, progress: true });

    const { response, error } = await apartmentServices.createMeeting(initialData?._id);
    if (error) {
      setRecordZoomMeetingModalMeta({ ...recordZoomMeetingModalMeta, progress: false });
      return toast.error('Error starting meeting');
    }

    setApartmentExtraInfo({
      ...apartmentExtraInfo,
      zoomMeetId: response?.zoomMeetId,
      zoomUserId: response?.zoomUserId,
      zoomMeetLink: response?.zoomMeetLink
    });
    setRecordZoomMeetingModalMeta({ ...recordZoomMeetingModalMeta, progress: false, show: false });
    window.open(response?.zoomMeetLink, '_blank');
  };

  const csvFileName = useMemo(() => {
    return `apartment-data-${new Date()
      .toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      .replace(/\//g, '-')}-${apartmentExtraInfo?._id}.csv`;
  }, [apartmentExtraInfo]);

  const handleCsvDownload = () => {
    const csvData = transformApartmentDataToCsv({
      fields: csvDataFields?.fields,
      values: updatedApartmentValues || csvDataFields?.values,
      notes: apartmentExtraInfo?.notes,
      translate,
      apartmentType
    });
    downloadFile({ data: csvData, fileName: csvFileName, type: 'text/csv' });
  };

  const handleSelectMatchingCustomers = (customer, profile) => {
    const customerExists = apartmentExtraInfo?.profileAssignments.find(c => c?.customer?._id === customer._id);
    if (customerExists) {
      setApartmentExtraInfo(prev => ({
        ...prev,
        profileAssignments: prev?.profileAssignments?.filter(c => c?.customer?._id !== customer._id)
      }));
    } else {
      const status = apartmentExtraInfo?.status;
      const canAssign = status === APARTMENT_STATUS_LABELS.available || status === APARTMENT_STATUS_LABELS.hot;
      if (!canAssign) {
        return toast.info(
          'Apartment cannot be assigned in its current status. Please ensure the apartment status is set to Available or Hot.'
        );
      }
      setApartmentExtraInfo(prev => ({
        ...prev,
        profileAssignments: [
          ...prev?.profileAssignments,
          {
            customer,
            status: 'Assigned',
            profile: profile || customer?.apartmentProfiles[0]?._id,
            assignmentDate: new Date()
          }
        ]
      }));
    }
  };

  const ApartmentOptions = useCallback(() => {
    return (
      <div className="mx-2 d-flex align-items-center gap-1">
        <>
          {editMode ? (
            <>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white"
                variant={'outline-primary-new'}
                onClick={() => onDuplicateApartmentClick(initialData)}
              >
                <Files />
                <h6 className="mb-0 smallFont">{translate('duplicate_apartment')}</h6>
              </Button>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white"
                variant={'outline-primary-new'}
                onClick={() => setRecordZoomMeetingModalMeta({ ...recordZoomMeetingModalMeta, show: true })}
                disabled={recordZoomMeetingModalMeta?.show}
              >
                {recordZoomMeetingModalMeta?.progress ? <Spinner animation="border" size="sm" /> : <CameraVideo />}
                <h6 className="mb-0 smallFont">{translate('start_new_meeting')}</h6>
              </Button>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white"
                variant={'outline-success'}
                onClick={handleViewOrCreateReportModal}
              >
                <FileEarmarkBarGraph />
                <h6 className="mb-0 smallFont">
                  {apartmentExtraInfo?.report ? translate('view_report') : translate('generate_report')}
                </h6>
              </Button>
              {apartmentExtraInfo?.report?.status === 'draft' && (
                <Button
                  size="sm"
                  className="d-flex align-items-center gap-1 hover-text-white"
                  variant={'outline-success'}
                  onClick={() =>
                    setSelectReportTemplateModalMeta({
                      ...selectReportTemplateModalMeta,
                      show: true,
                      regenerateReportMode: true,
                      apartmentExtraInfo
                    })
                  }
                >
                  <LightningChargeFill />
                  <h6 className="mb-0 smallFont">{translate('regenerate_report')}</h6>
                </Button>
              )}
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white"
                variant={'outline-success'}
                onClick={() => setLogModalMeta({ show: true, id: initialData?._id })}
              >
                <FileText />
                <h6 className="mb-0 smallFont">{translate('logs')}</h6>
              </Button>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white"
                variant={'outline-success'}
                onClick={handleCsvDownload}
              >
                <FiletypeCsv />
                <h6 className="mb-0 smallFont">CSV</h6>
              </Button>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1"
                variant={openDriveManager ? 'outline-danger' : 'outline-dark'}
                onClick={onDriveFolderClick}
              >
                <Folder />
                <h6 className="mb-0 smallFont">{translate(openDriveManager ? 'close_drive' : 'open_drive')}</h6>
              </Button>
              <Button
                size="sm"
                className="d-flex align-items-center gap-1"
                variant={'dark'}
                onClick={() => setViewCustomersMatchingApartments(true)}
              >
                <PersonCircle />
                <h6 className="mb-0 smallFont">{translate('viewing_matching_customers')}</h6>
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              className="d-flex align-items-center gap-1"
              variant={'outline-dark'}
              onClick={createNewFolder}
              disabled={creatingNewFolder || showProgress || isSavingOnly}
            >
              {creatingNewFolder ? <CircularProgressBar size={1.2} /> : <Folder />}
              <h6 className="mb-0 smallFont">{creatingNewFolder ? 'Creating folder' : 'Create folder'}</h6>
            </Button>
          )}
        </>
      </div>
    );
  }, [editMode, createNewFolder, showProgress, creatingNewFolder, onDriveFolderClick, openDriveManager, isSavingOnly]);

  const handleArchiveApartmentClick = useCallback(() => {
    setArchiveAlertMeta({ show: true, loading: false });
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    const newisApartmentArchived = !apartmentExtraInfo?.isApartmentArchived;
    const apartmentId = apartmentExtraInfo?._id;

    if (!apartmentId) {
      toast.error('Apartment ID is missing');
      setArchiveAlertMeta({ show: false, loading: false });
      return;
    }

    setArchiveAlertMeta({ show: true, loading: true });

    const { response, error } = await apartmentServices.updateApartment(apartmentId, {
      isApartmentArchived: newisApartmentArchived
    });

    if (response) {
      toast.success(
        newisApartmentArchived ? translate('apartment_archived_success') : translate('apartment_unarchived_success')
      );
      // call the onArchiveApartmentStatusChangeSuccess callback to update the apartment table
      onArchiveApartmentStatusChangeSuccess(response._id, response.status);
    }
    if (error) {
      toast.error(error.message || 'Failed to update apartment archive status');
      setArchiveAlertMeta({ show: false, loading: false });
      return;
    }

    setApartmentExtraInfo(prev => ({
      ...prev,
      isApartmentArchived: newisApartmentArchived
    }));
    setArchiveAlertMeta({ show: false, loading: false });
  }, [apartmentExtraInfo?.isApartmentArchived, apartmentExtraInfo?._id]);

  const ModalFooter = useCallback(() => {
    return (
      <div className="w-100 d-flex justify-content-between align-items-center mx-2">
        <div className="d-flex align-items-center gap-1">
          {editMode && (
            <>
              <Button
                size="sm"
                disabled={showProgress || creatingDriveFolder || creatingNewFolder || isSavingOnly}
                className="d-flex align-items-center gap-1"
                variant="danger"
                onClick={() => onDeleteApartmentClick(initialData)}
              >
                <Trash /> <h6 className="mb-0 smallFont">{translate('delete')}</h6>
              </Button>
              {/* // show archive button only when apartment is loaded */}
              {apartmentExtraInfo?._id && (
                <Button
                  size="sm"
                  variant="gray"
                  disabled={showProgress || creatingDriveFolder || creatingNewFolder || isSavingOnly}
                  className="d-flex align-items-center gap-1 text-white"
                  onClick={handleArchiveApartmentClick}
                >
                  <Archive />
                  <h6 className="mb-0 smallFont">
                    {apartmentExtraInfo?.isApartmentArchived
                      ? translate('unarchive') || 'Unarchive'
                      : translate('archive') || 'Archive'}
                  </h6>
                </Button>
              )}
            </>
          )}
        </div>
        <div className="d-flex align-items-center gap-1">
          <Button
            disabled={showProgress || creatingDriveFolder || creatingNewFolder || isSavingOnly}
            size="sm"
            className="text-white"
            variant="danger"
            onClick={() => {
              onHide();
              setCsvDataFields(null);
            }}
          >
            {translate('close')}
          </Button>
          <Button
            disabled={showProgress || creatingDriveFolder || creatingNewFolder || isSavingOnly}
            size="sm"
            className="text-white"
            variant="primary"
            onClick={() => saveApartment(false)}
          >
            {translate(isSavingOnly ? 'please_wait' : 'save')}
          </Button>
          <Button
            disabled={showProgress || creatingDriveFolder || creatingNewFolder || isSavingOnly}
            size="sm"
            className="text-white"
            variant="success"
            onClick={() => saveApartment(true)}
          >
            {translate(showProgress ? 'please_wait' : 'save_and_close')}
          </Button>
        </div>
      </div>
    );
  }, [
    show,
    editMode,
    onDeleteApartmentClick,
    initialData,
    apartment,
    showProgress,
    isSavingOnly,
    apartmentExtraInfo,
    saveApartment,
    creatingDriveFolder,
    onHide,
    creatingNewFolder,
    translate,
    handleArchiveApartmentClick
  ]);

  useEffect(() => {
    if (editMode && apartmentExtraInfo?._id) {
      fetchCustomersMatchingApartments();
    }
  }, [editMode, apartmentExtraInfo?._id]);

  if (!show) return;

  const handleGeneratedReport = template => {
    setApartmentExtraInfo({ ...apartmentExtraInfo, showEditReport: true, report: template });
    handleApartmentAfterReportCreateOrUpdate(apartmentExtraInfo?._id, {
      reportId: { _id: template?._id, status: 'draft' }
    });
  };

  return (
    <>
      <AppModal
        fullscreen
        modalBodyClassName="p-0"
        show={show}
        onHide={() => {
          onHide();
          setCsvDataFields(null);
        }}
        title={translate(!editMode ? 'add_apartment' : 'update_apartment')}
        footerContent={ModalFooter}
        renderMoreInHeader={ApartmentOptions}
      >
        {openDriveManager ? (
          <div className="p-2 h-100">
            {apartmentExtraInfo?.driveFolder ? (
              <GoogleDriveManager googleDriveFolderId={apartmentExtraInfo?.driveFolder} />
            ) : (
              <AddGoogleDrive creatingDriveFolder={creatingDriveFolder} onAddDriveFolder={onCreateDriveFolderClick} />
            )}
          </div>
        ) : (
          <AddEditApartmentForm
            handleSelectCustomer={selectCustomers}
            editMode={editMode}
            apartmentType={apartmentType}
            apartmentValues={apartmentValues}
            setApartment={setApartment}
            adminCompTable={adminCompTable}
            apartmentExtraInfo={apartmentExtraInfo}
            setApartmentExtraInfo={setApartmentExtraInfo}
            customerPickerModalMeta={customerPickerModalMeta}
            setCustomerPickerModalMeta={setCustomerPickerModalMeta}
            assigneUsers={assigneUsers}
            handleApartmentAfterNoteUpdate={handleApartmentAfterNoteUpdate}
            setCsvDataFields={setCsvDataFields}
            saveApartment={saveApartment}
          />
        )}
        <ViewCustomersMatchingApartmentModal
          show={viewCustomersMatchingApartment}
          onHide={() => setViewCustomersMatchingApartments(false)}
          customers={customersMatchingApartment}
          showProgress={fetchingCustomersMatchingApartments}
          onUpdateCustomerProfile={onUpdateCustomerProfile}
          selectedMatchingCustomers={apartmentExtraInfo?.profileAssignments?.map(p => p?.customer)}
          onSelectMatchingCustomers={handleSelectMatchingCustomers}
        />
      </AppModal>
      <GenerateOrRegenerateReportModal
        show={selectReportTemplateModalMeta?.show}
        regenerateReportMode={selectReportTemplateModalMeta?.regenerateReportMode}
        onHide={() => setSelectReportTemplateModalMeta(null)}
        apartmentExtraInfo={selectReportTemplateModalMeta?.apartmentExtraInfo}
        onSubmit={handleGeneratedReport}
      />
      <EditApartmentReportModal
        show={apartmentExtraInfo?.showEditReport}
        onHide={() => setApartmentExtraInfo({ ...apartmentExtraInfo, showEditReport: false })}
        apartmentReport={apartmentExtraInfo?.report}
        onChange={report => setApartmentExtraInfo({ ...apartmentExtraInfo, report })}
        onDelete={() => {
          setApartmentExtraInfo({ ...apartmentExtraInfo, report: null });
          handleApartmentAfterReportCreateOrUpdate(apartmentExtraInfo?._id, {
            reportId: null
          });
        }}
        onUpdateStatus={status => {
          setApartmentExtraInfo({ ...apartmentExtraInfo, report: { ...apartmentExtraInfo?.report, status } });
          handleApartmentAfterReportCreateOrUpdate(apartmentExtraInfo?._id, {
            reportId: { _id: apartmentExtraInfo?.report?._id, status }
          });
        }}
      />

      <RecordZoomMeetingModal
        show={recordZoomMeetingModalMeta?.show}
        onHide={() => setRecordZoomMeetingModalMeta(null)}
        onConfirm={handleRecordZoomMeeting}
        progress={recordZoomMeetingModalMeta?.progress}
      />
      <LogModal show={logModalMeta?.show} onHide={() => setLogModalMeta(null)} id={logModalMeta?.id} />
      <AlertModal
        show={archiveAlertMeta.show}
        onHide={() => !archiveAlertMeta.loading && setArchiveAlertMeta({ show: false, loading: false })}
        alertText={
          apartmentExtraInfo?.isApartmentArchived
            ? translate('confirm_unarchive_apartment_text')
            : translate('confirm_archive_apartment_text')
        }
        onContinueClick={handleConfirmArchive}
        onDismissClick={() => !archiveAlertMeta.loading && setArchiveAlertMeta({ show: false, loading: false })}
        onContinue2Click={() => !archiveAlertMeta.loading && setArchiveAlertMeta({ show: false, loading: false })}
        continue2ButtonText={translate('no')}
        continue2ButtonVariant="danger"
        showProgress={archiveAlertMeta.loading}
        progressText={
          apartmentExtraInfo?.isApartmentArchived
            ? translate('unarchiving_apartment')
            : translate('archiving_apartment')
        }
      />
    </>
  );
};

export default AddEditApartmentModal;

const GenerateOrRegenerateReportModal = ({ onSubmit, show, onHide, apartmentExtraInfo, regenerateReportMode }) => {
  const { translate } = useLocalization();
  const [reportTemplates, setReportTemplates] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [progress, setProgress] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [apartmentExtraInfo, show]);

  const fetchTemplates = async () => {
    const { response, error } = await reportService.getReportTemplates();
    if (error) {
      toast.error(error);
      setReportTemplates([]);
    } else {
      setReportTemplates(response.results);
    }
  };

  const handleGenerateOrRegenerateReport = async () => {
    setProgress(true);
    const { response, error } = await reportService.createReport({
      templateId: regenerateReportMode ? apartmentExtraInfo?.report?.templateId : selectedTemplate.value,
      apartmentId: apartmentExtraInfo?._id
    });
    if (error) {
      setProgress(false);
      return toast.error(error);
    }
    response && onSubmit(response.data);
    response && onHide();
    response && setProgress(false);
  };

  if (regenerateReportMode) {
    return (
      <AlertModal
        show={show}
        onHide={onHide}
        alertText={translate('confirm_regenerate_report')}
        onSubmit={handleGenerateOrRegenerateReport}
        showProgress={progress}
        progressText={translate('regenerate_report_progress')}
        continueButtonText={translate('regenerate_report')}
        continueButtonVariant="success text-white"
        continueButtonSize="sm"
        onContinueClick={handleGenerateOrRegenerateReport}
      />
    );
  }

  return (
    <AppModal
      modalBodyClassName="py-2 px-3"
      title={translate('generate_report')}
      show={show}
      onHide={() => {
        setProgress(false);
        onHide();
        setSelectedTemplate(null);
      }}
    >
      <Form>
        <FormGroup>
          <FormLabel className="mid mb-1">{translate('select_report_template')}</FormLabel>

          <CustomMultiSelect
            items={reportTemplates?.map(t => ({
              label: t.name,
              _id: t._id,
              value: t._id
            }))}
            closeMenuOnSelect
            isMulti={false}
            selectedItems={selectedTemplate?.value || ''}
            onChange={template =>
              setSelectedTemplate({
                value: template,
                template: reportTemplates.find(t => t._id === template)
              })
            }
          />
        </FormGroup>
        {progress && <HorizontalProgress text={translate('generating_report')} />}
        <div className="mt-2 text-end">
          <Button
            disabled={Boolean(selectedTemplate) === false || progress}
            onClick={handleGenerateOrRegenerateReport}
            variant="success text-white"
            size="sm"
          >
            {translate('generate')}
          </Button>
        </div>
      </Form>
    </AppModal>
  );
};
